import {AddressModel} from '../../../../domain/models/checkout/Cart/Address.model';
import {ContactModel} from '../../../../domain/models/checkout/Cart/Contact.model';
import {CustomFieldModel} from '../../../../domain/models/checkout/Cart/CustomField.model';
import {CheckoutSettingsModel} from '../../../../domain/models/checkout/checkoutSettings/CheckoutSettings.model';
import {
  AddressTarget,
  CustomerDetailsFormOverrides,
  CustomerDetailsFormSection,
  CustomerDetailsFormValues,
  FormFieldTarget,
  FormHeaderId,
  FullNameTarget,
} from '../../../../types/checkoutApp.types';
import {DEFAULT_COUNTRY} from '../../constants';
import {
  getDeliveryCountriesOverrides,
  getExcludeGoogleAutoCompleteOverrides,
  getSettingsOverrides,
  mergeOverrides,
  getAddressFormInitialState,
  getContactFormInitialState,
  getRequiredCountryOverride,
} from '../form.utils';
import {FormValues} from '@wix/form-viewer';
import {ExtendedFieldsFragment} from '../../../../gql/graphql';
import {UserExtendedFields, isExtendedFieldNamespacesIsUserFields} from '../../../../domain/utils/types.util';
import {type CountryCode} from '@wix/form-multiline-address';

export function getCustomerDetailsFormInitialState({
  checkoutSettings,
  address,
  email,
  contact,
  customField,
  extendedFields,
  doesFormHaveMultilineAddress,
}: {
  checkoutSettings: CheckoutSettingsModel;
  address?: AddressModel;
  initialAddress?: AddressModel;
  email?: string;
  contact?: ContactModel;
  customField?: CustomFieldModel;
  extendedFields?: ExtendedFieldsFragment;
  doesFormHaveMultilineAddress?: boolean;
}): CustomerDetailsFormValues {
  let country;
  const enabledDeliveryCountries = checkoutSettings.enabledDeliveryCountries;
  if (!enabledDeliveryCountries?.length || enabledDeliveryCountries.includes(DEFAULT_COUNTRY)) {
    country = address?.country ?? DEFAULT_COUNTRY;
  } else {
    country = address?.country ?? enabledDeliveryCountries[0];
  }

  return {
    ...(email !== undefined ? {email} : {}),
    ...getContactFormInitialState({
      contact,
      country,
    }),
    ...(extendedFields ? getExtendedFieldsFromCheckout(extendedFields) : {}),
    ...getAdditionalInfoFormInitialState(customField, doesFormHaveMultilineAddress),
    ...getAddressFormInitialState(address, country, doesFormHaveMultilineAddress),
  };
}

export function validateEmailAddress(email: string): boolean {
  const emailRegex =
    /^(?!.*\.\.)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

export function getEmailFromEmailFormValues(emailFormValues: FormValues): string {
  return emailFormValues.email as string;
}

export function getSubscriptionFromFormValues(formValues: FormValues): boolean {
  return formValues[FormFieldTarget.subscribe] as boolean;
}

function getAdditionalInfoFormInitialState(
  customField?: CustomFieldModel,
  doesFormHaveMultilineAddress?: boolean
): FormValues {
  if (doesFormHaveMultilineAddress) {
    return customField?.value
      ? {
          additional_info_1: customField?.value,
        }
      : {};
  }

  return {
    additional_info_1: customField?.value ?? '',
  };
}

export function getCustomFieldFromAdditionalInfoFormValues(additionalInfoFormValues: FormValues): string {
  return (additionalInfoFormValues.additional_info_1 as string) ?? undefined;
}

function getHeadersOverrides({
  isShippingFlow,
  isMember,
  isFastFlow,
}: {
  isShippingFlow: boolean;
  isMember: boolean;
  isFastFlow?: boolean;
}): CustomerDetailsFormOverrides {
  return {
    [FormHeaderId.customer_details_header]: {
      hidden: true,
    },
    [FormHeaderId.shipping_details_header]: {
      hidden: !isShippingFlow || isMember || isFastFlow,
    },
  };
}

function getCheckoutFlowsOverrides({
  isShippingFlow,
  isMember,
  formSections,
  doesFormHaveMultilineAddress,
}: {
  isShippingFlow: boolean;
  isMember: boolean;
  formSections: CustomerDetailsFormSection[];
  doesFormHaveMultilineAddress?: boolean;
}): CustomerDetailsFormOverrides {
  /*istanbul ignore next*/
  const additionalInfoSectionOverride = !formSections.includes(CustomerDetailsFormSection.additionalInfo)
    ? {[FormFieldTarget.additional_info_1]: {hidden: true, required: false}}
    : {};

  /*istanbul ignore next*/
  const subscriptionSectionOverride = !formSections.includes(CustomerDetailsFormSection.subscription)
    ? {[FormFieldTarget.subscribe]: {hidden: true, required: false}}
    : {};

  /*istanbul ignore next*/
  const contactSectionOverride = !formSections.includes(CustomerDetailsFormSection.contact)
    ? {
        [FormFieldTarget.full_name]: {
          [FullNameTarget.first_name]: {hidden: true, required: false},
          [FullNameTarget.last_name]: {hidden: true, required: false},
        },
        [FormFieldTarget.phone]: {hidden: true, required: false},
        [FormFieldTarget.company_name]: {hidden: true, required: false},
      }
    : {};

  const emailSectionOverride =
    isMember || !formSections.includes(CustomerDetailsFormSection.email)
      ? {[FormFieldTarget.email]: {hidden: true, required: false}}
      : {};

  const addressSectionOverride =
    !isShippingFlow || /*istanbul ignore next*/ !formSections.includes(CustomerDetailsFormSection.address)
      ? {
          ...(doesFormHaveMultilineAddress
            ? {
                [FormFieldTarget.address]: {
                  [AddressTarget.addressLine]: {hidden: true, required: false},
                  [AddressTarget.addressLine2]: {hidden: true, required: false},
                  [AddressTarget.streetName]: {hidden: true, required: false},
                  [AddressTarget.streetNumber]: {hidden: true, required: false},
                  [AddressTarget.postalCode]: {hidden: true, required: false},
                  [AddressTarget.city]: {hidden: true, required: false},
                  [AddressTarget.country]: {hidden: true, required: false},
                  [AddressTarget.subdivision]: {hidden: true, required: false},
                },
              }
            : {
                [FormFieldTarget.address]: {
                  [AddressTarget.address_line]: {hidden: true, required: false},
                  [AddressTarget.address_line_2]: {hidden: true, required: false},
                  [AddressTarget.street_name]: {hidden: true, required: false},
                  [AddressTarget.street_number]: {hidden: true, required: false},
                  [AddressTarget.postal_code]: {hidden: true, required: false},
                  [AddressTarget.city]: {hidden: true, required: false},
                  [AddressTarget.country]: {hidden: true, required: false},
                  [AddressTarget.subdivision]: {hidden: true, required: false},
                },
              }),
        }
      : {};

  return mergeOverrides(
    emailSectionOverride,
    contactSectionOverride,
    addressSectionOverride,
    additionalInfoSectionOverride,
    subscriptionSectionOverride
  );
}

export function getCustomerDetailsFormOverrides({
  checkoutSettings,
  excludeGoogleAutoComplete,
  country,
  isShippingFlow,
  isMember,
  formSections,
  isFastFlow,
  doesFormHaveMultilineAddress,
  moveCheckoutToViewerContext,
}: {
  checkoutSettings: CheckoutSettingsModel;
  excludeGoogleAutoComplete: boolean;
  country?: CountryCode | null;
  isShippingFlow: boolean;
  isMember: boolean;
  formSections?: CustomerDetailsFormSection[];
  isFastFlow?: boolean;
  doesFormHaveMultilineAddress?: boolean;
  moveCheckoutToViewerContext: boolean;
}): CustomerDetailsFormOverrides {
  const deliveryCountriesOverrides = getDeliveryCountriesOverrides(checkoutSettings.enabledDeliveryCountries);
  const formSectionsForOverrides = formSections ?? [
    CustomerDetailsFormSection.email,
    CustomerDetailsFormSection.subscription,
    CustomerDetailsFormSection.contact,
    CustomerDetailsFormSection.address,
    CustomerDetailsFormSection.additionalInfo,
  ];

  return mergeOverrides(
    getSettingsOverrides({checkoutSettings, doesFormHaveMultilineAddress}),
    deliveryCountriesOverrides,
    getHeadersOverrides({isShippingFlow, isMember, isFastFlow}),
    getExcludeGoogleAutoCompleteOverrides({
      excludeGoogleAutoComplete,
      country,
      moveCheckoutToViewerContext,
      doesFormHaveMultilineAddress,
    }),
    getCheckoutFlowsOverrides({
      isShippingFlow,
      isMember,
      formSections: formSectionsForOverrides,
      doesFormHaveMultilineAddress,
    }),
    getRequiredCountryOverride({
      isShippingFlow,
      formSections: formSectionsForOverrides,
      doesFormHaveMultilineAddress,
    })
  );
}

export function getExtendedFieldsFromCheckout(extendedFields?: UserExtendedFields) {
  return extendedFields?.namespaces && isExtendedFieldNamespacesIsUserFields(extendedFields?.namespaces)
    ? extendedFields.namespaces._user_fields
    : /* istanbul ignore next */ undefined;
}

export function getExtendedFieldValuesFromCustomerDetailsForm(
  formValues: FormValues,
  dExtTargets: string[] | null
): FormValues {
  /*istanbul ignore next*/
  if (!dExtTargets) {
    /* istanbul ignore next */
    return {};
  }
  return dExtTargets.reduce(
    /* istanbul ignore next */
    (dExtValues, target) => ({
      ...dExtValues,
      [target]: formValues[target],
    }),
    {}
  );
}
